import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PANEL_CLASS = "rounded-lg border border-[var(--exed-card-border)] bg-[var(--exed-card-bg)]";
const TRIGGER_CLASS = "gap-2.5 px-4 py-3.5 hover:no-underline text-left font-brand text-[15px] font-semibold text-[var(--exed-ink-900)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--exed-ink-900)]";

/** Selo de contagem; o " · " fica só para leitor de tela, para o nome acessível seguir "Título · N". */
function CountPill({ count, className }: { count: number; className: string }) {
  return (
    <>
      <span className="sr-only"> · </span>
      <span className={`rounded-full px-2 py-0.5 font-sans text-xs font-semibold ${className}`}>
        {count.toLocaleString("en-US")}
      </span>
    </>
  );
}

function DecidedTitle({ dot, title, count, pill }: { dot: string; title: string; count: number; pill: string }) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
      <span>{title}</span>
      <CountPill count={count} className={pill} />
    </span>
  );
}

export function TransferLayout({
  pendingTitle,
  pendingCount,
  pendingActions,
  pending,
  approvedCount,
  approved,
  outOfScopeCount,
  outOfScope,
  pendingOnly = false,
  decidedOnly = false,
}: {
  /** Modo Mapa: o mapa já mostra as decisões, então os painéis da direita saem. */
  pendingOnly?: boolean;
  /**
   * Sem pendentes (15/10): o painel vazio sai e Aprovados e Fora do escopo dividem
   * a largura, lado a lado. Voltar algo para pendente devolve o layout normal.
   */
  decidedOnly?: boolean;
  pendingTitle: string;
  pendingCount: number;
  pendingActions?: ReactNode;
  pending: ReactNode;
  approvedCount: number;
  approved: ReactNode;
  outOfScopeCount: number;
  outOfScope: ReactNode;
}) {
  const { t } = useTranslation();
  const pendingTitleId = useId();
  const approvedPanel = (
    <AccordionItem value="approved" className={`overflow-hidden ${PANEL_CLASS}`}>
      <AccordionTrigger className={TRIGGER_CLASS} aria-label={`${t("Approved")} · ${approvedCount}`}>
        <DecidedTitle dot="bg-[var(--exed-success)]" title={t("Approved")} count={approvedCount} pill="bg-[rgba(39,161,85,0.1)] text-[#1e7d42]" />
      </AccordionTrigger>
      <AccordionContent className="pb-0">{approved}</AccordionContent>
    </AccordionItem>
  );
  const outPanel = (
    <AccordionItem value="out_of_scope" className={`overflow-hidden ${PANEL_CLASS}`}>
      <AccordionTrigger className={TRIGGER_CLASS} aria-label={`${t("Out of scope")} · ${outOfScopeCount}`}>
        <DecidedTitle dot="bg-[var(--exed-danger)]" title={t("Out of scope")} count={outOfScopeCount} pill="bg-[rgba(212,53,60,0.08)] text-[#a3262c]" />
      </AccordionTrigger>
      <AccordionContent className="pb-0">{outOfScope}</AccordionContent>
    </AccordionItem>
  );
  if (decidedOnly) {
    return (
      <Accordion type="multiple" defaultValue={["approved", "out_of_scope"]} data-testid="decided-only" className="grid items-start gap-4 lg:grid-cols-2">
        {approvedPanel}
        {outPanel}
      </Accordion>
    );
  }
  return (
    <div className={pendingOnly ? "grid items-start gap-4" : "grid items-start gap-4 lg:grid-cols-[minmax(0,1.85fr)_minmax(340px,1fr)]"}>
      <section data-pending-panel aria-labelledby={pendingTitleId} className={`min-w-0 ${PANEL_CLASS}`}>
        <header className="flex min-h-[40px] flex-wrap items-center justify-between gap-3 border-b border-[var(--exed-card-border)] px-5 py-4">
          <h2 tabIndex={-1} id={pendingTitleId} className="flex min-w-0 items-center gap-2.5 break-words font-brand text-base font-semibold text-[var(--exed-ink-900)]">
            {pendingTitle}
            <CountPill count={pendingCount} className="bg-[rgba(242,115,37,0.1)] text-[#c2410c]" />
          </h2>
          {pendingActions}
        </header>
        <div className="min-w-0">{pending}</div>
      </section>
      {!pendingOnly && <aside className="min-w-0 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:overflow-auto">
        <Accordion type="multiple" defaultValue={["approved", "out_of_scope"]} className="space-y-4">
          {approvedPanel}
          {outPanel}
        </Accordion>
      </aside>}
    </div>
  );
}
